import { useCallback, useRef, useState } from 'react';
import ReactCrop, { type Crop, type PixelCrop } from 'react-image-crop';

interface PhotoCropContentProps {
    photoUrl: string,
    subHeader: string,
    naturalWidth: number,
    naturalHeight: number,
    minCropSize: number,
    circularCrop: boolean,
    onSubmitCrop: (coords: CropCoordinates) => void,
    submitting: boolean,
}

export interface CropCoordinates {
    xCoord: number,
    yCoord: number,
    cropWidth: number,
    cropHeight: number,
    naturalWidth: number,
    naturalHeight: number,
}

const PhotoCropContent = ({
    photoUrl,
    subHeader,
    naturalWidth,
    naturalHeight,
    minCropSize,
    circularCrop,
    onSubmitCrop,
    submitting,
}: PhotoCropContentProps) => {
    const imgRef = useRef<HTMLImageElement>(null);
    const [crop, setCrop] = useState<Crop>();
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>();

    const onImageLoad = useCallback(() => {
        const img = imgRef.current;
        if (!img) return;

        // Set initial crop to a centered square using the smaller dimension
        const displayedWidth = img.width;
        const displayedHeight = img.height;
        const initialSize = Math.min(displayedWidth, displayedHeight);

        setCrop({
            unit: 'px',
            x: (displayedWidth - initialSize) / 2,
            y: (displayedHeight - initialSize) / 2,
            width: initialSize,
            height: initialSize,
        });
    }, []);

    const getNaturalDimensions = () => {
        const img = imgRef.current;
        if (!img) return { w: naturalWidth, h: naturalHeight };
        // Server-provided dimensions can be 0; fall back to the image element
        const w = naturalWidth || img.naturalWidth;
        const h = naturalHeight || img.naturalHeight;
        return { w, h };
    };

    const handleSubmit = () => {
        const img = imgRef.current;
        if (!img || !completedCrop) return;

        const { w, h } = getNaturalDimensions();
        const scaleX = w / img.width;
        const scaleY = h / img.height;

        onSubmitCrop({
            xCoord: Math.round(completedCrop.x * scaleX),
            yCoord: Math.round(completedCrop.y * scaleY),
            cropWidth: Math.round(completedCrop.width * scaleX),
            cropHeight: Math.round(completedCrop.height * scaleY),
            naturalWidth: w,
            naturalHeight: h,
        });
    };

    // Scale minCropSize from natural pixels to displayed pixels
    const { w: natW } = getNaturalDimensions();
    const displayedMinCrop = imgRef.current && natW
        ? Math.round(minCropSize * (imgRef.current.width / natW))
        : minCropSize;

    return (
        <div className="photo-crop-content">
            <h3>Crop your photo</h3>
            <p className="subheader">{subHeader}</p>
            <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={1}
                circularCrop={circularCrop}
                minWidth={displayedMinCrop}
                minHeight={displayedMinCrop}
            >
                <img
                    ref={imgRef}
                    src={photoUrl}
                    alt=""
                    onLoad={onImageLoad}
                    crossOrigin="anonymous"
                    style={{ maxWidth: '100%', maxHeight: '500px' }}
                />
            </ReactCrop>
            <div className="photo-crop-actions">
                <button
                    className="button-primary"
                    type="button"
                    onClick={handleSubmit}
                    disabled={!completedCrop || submitting}
                >
                    {submitting ? 'Saving...' : 'Save crop'}
                </button>
            </div>
        </div>
    );
};

export default PhotoCropContent;
